import { useEffect, useState } from 'react';
import { IconArrowLeft, IconBell, IconChecklist, IconDots, IconFileText, IconLock, IconPlus, IconSearch, IconSettings } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useT } from '@hana/i18n';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { DECISION_COLS, DECISION_TEMPLATES, decisionSoon, DECISION_MASK, DECISION_MORE, DECISION_STATUS, DECISION_TABS, decisionIsMoney, decisionKpis, decisionTypeLabel, filterDecisions, tabCount, type DecisionTab } from './decision';
import { DECISION_TYPES } from './fixtures';
import type { DecisionType } from './types';
import './decision.i18n';
import s from './hr.module.css';
import d from './decision.module.css';

// Mint-HRDecision · danh sách (design-specs/Mint-HRDecision.md). Chỉ đọc; nút ghi báo toast. Không lương: loại có tiền che nội dung.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const TONE = { warn: s.tWarn ?? '', ok: s.tOk ?? '', err: s.tErr ?? '', mute: s.tMute ?? '' };

export function HrDecision() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, error, isLoading, isError, refetch } = useList('hrDecision', { limit: 100 });
  const forced = readState();
  const [tab, setTab] = useState<DecisionTab>('pending');
  const [type, setType] = useState<DecisionType | 'all'>('all');
  const [q, setQ] = useState('');
  const [dense, setDense] = useState<'std' | 'compact'>('std');
  const [more, setMore] = useState(false);
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const all = data?.items ?? [];

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';

  const noWrite = () => toast({ msg: t('hrd.noWrite') });
  const rows = filterDecisions(all, { tab, type, q });
  const tabs = DECISION_TABS.map((x) => ({ key: x.key, label: x.label, n: tabCount(all, x.key) }));
  const types = [{ key: 'all', label: t('hrd.allTypes') }, ...DECISION_TYPES.map((x) => ({ key: x.type, label: x.label }))];

  return (
    <Page
      kind="table"
      dock={wide ? (
        <div className={d.dock}>
          <h2>{t('hrd.tplHd')}</h2>
          <div className={d.cap}>{t('hrd.tplCap')}</div>
          {DECISION_TEMPLATES.map((m) => (
            <div key={m.code} className={d.tpl}>
              <IconFileText size={16} stroke={1.9} aria-hidden="true" />
              <div><b>{m.code}</b><div>{m.k}</div></div>
              <span className={`${s.stChip} ${TONE.mute}`}>Draft</span>
            </div>
          ))}
          <button type="button" className={d.link} onClick={() => void navigate({ to: '/ezy/hr/contract' })}>
            <IconSettings size={16} stroke={1.8} aria-hidden="true" />{t('hrd.manageTpl')}
          </button>
          <h2 className={d.h2b}>{t('hrd.soon')}</h2>
          {decisionSoon(all).map((x) => (
            <div key={x.k} className={d.soon}><b>{x.k}</b><div>{x.sub}</div></div>
          ))}
        </div>
      ) : undefined}
    >
      <PageHeader
        title={t('hrd.title')}
        lead={<HeaderTool label={t('hrd.backHr')} icon={IconArrowLeft} onClick={() => void navigate({ to: '/ezy/hr' })} />}
        actions={
          <>
            <Segmented kind="toggle" label={t('hrd.density')} value={dense} onChange={setDense} items={[{ key: 'std', label: t('hrd.dStd') }, { key: 'compact', label: t('hrd.dCompact') }]} />
            <HeaderTool label={t('hrd.notif')} icon={IconBell} onClick={() => toast({ msg: t('hrd.notifToast') })} />
            {!wide && <HeaderTool label={t('hrd.tplHd')} icon={IconChecklist} onClick={() => toast({ msg: t('hrd.tplToast') })} />}
            <HeaderTool label={t('hrd.more')} icon={IconDots} active={more} onClick={() => setMore((v) => !v)} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('hrd.create')}</Button>
          </>
        }
      />
      {more && (
        <div role="menu" aria-label={t('hrd.more')} className={s.card}>
          {DECISION_MORE.map(([k, sub]) => (
            <button key={k} type="button" role="menuitem" className={s.evAct} onClick={() => { setMore(false); toast({ msg: t('hrd.kNoWrite', { k }) }); }} title={sub}>{k}</button>
          ))}
        </div>
      )}
      <div className={s.page}>
        <div className={s.tabs}>
          <Segmented label={t('hrd.title')} value={tab} onChange={setTab} items={tabs} />
        </div>
        <StateView
          state={state}
          title={state === 'empty' ? t('hrd.emptyTitle') : undefined}
          desc={state === 'empty' ? t('hrd.emptyDesc') : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('hrd.retry')}</Button> : state === 'empty' ? <Button variant="primary" onClick={noWrite}>{t('hrd.createPlus')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          <div className={s.kpis}>
            {decisionKpis(all).map((k) => (
              <div key={k.k} className={s.kpi}>
                <div className={s.kpiK}>{k.k}</div>
                <div className={s.kpiV}>
                  <b className={k.tone === 'warn' ? s.warn : undefined}>{k.v}</b>
                  <span>{k.u}</span>
                </div>
              </div>
            ))}
          </div>
          <div className={`${s.sfBar} ${d.bar}`}>
            <label className={s.sfSearch} style={{ width: 300 }}>
              <IconSearch size={16} stroke={2.2} aria-hidden="true" />
              <input aria-label={t('hrd.search')} placeholder={t('hrd.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <div className={d.typeBar}>
              <Segmented kind="toggle" label={t('hrd.filterType')} value={type} onChange={(k) => setType(k as DecisionType | 'all')} items={types} />
            </div>
            <span className={s.sfCount}>{t('hrd.count', { n: rows.length, m: all.length })}</span>
          </div>
          <section className={s.card} aria-label={t('hrd.ledger')} style={{ padding: 0 }}>
            <table className={`${s.table} ${d.dTbl}`} aria-label={t('hrd.ledger')}>
              <colgroup>{DECISION_COLS.map(([k, w]) => <col key={k} style={{ width: w }} />)}</colgroup>
              <thead>
                <tr>{DECISION_COLS.map(([k]) => <th key={k} scope="col">{k}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = DECISION_STATUS[r.status];
                  const locked = decisionIsMoney(r.type);
                  return (
                    <tr key={r.no} className={s.sfRow} onClick={() => void navigate({ to: '/ezy/hr/decision/$no', params: { no: r.no } })}>
                      <td><b>{r.no}</b><div>{decisionTypeLabel(r.type)}</div></td>
                      <td>{r.name}<div>{r.user} · {r.seat}</div></td>
                      <td className={locked ? d.locked : d.after} title={locked ? DECISION_MASK : r.after}>
                        <span>{locked && <IconLock size={12} stroke={1.9} aria-hidden="true" />}{locked ? DECISION_MASK : r.after}</span>
                      </td>
                      <td>{r.effective}</td>
                      <td><span className={`${s.stChip} ${TONE[st.tone]}`} title={r.status}>{st.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 && <div className={d.empty}>{t('hrd.noMatch')}</div>}
          </section>
        </StateView>
      </div>
    </Page>
  );
}
